import { useTranslation } from './i18n';
import { useCredits } from './use-credits';
import { usePlans, usePremium } from '../components/settings/shared/settings-queries';
import { activePlanIsMaxPlus } from '../components/settings/shared/max-plus';
import { activePremiumSubscription } from '../components/settings/shared/settings-plan';
import { planNameKeyOf } from '../components/settings/shared/settings-shared';

/**
 * The plan facts the low/out-of-credits widgets need for their COPY — the account's plan name (for the
 * monthly "…on your {planName} plan" line), when its credits renew, and whether it is the top ("Max+")
 * tier (which only swaps the CTA label to "Add Credits" — the title/body are the same paid copy).
 *
 * `planName` is resolved EXACTLY as the drawer credits card resolves it (`useCreditsSummary`): the
 * catalog name of the active paid subscription, falling back to the overview's own `plan.name`, then a
 * generic "Your plan" — so the widget names the plan the same way the card and the account page do. It
 * is only ever rendered for a monthly (paid/Max+) account; a free/daily account never shows it.
 */
export interface CreditPlanContext {
  isFreePlan: boolean;
  isMaxPlus: boolean;
  /** The account's own plan name, localized (dynamic — never a hardcoded "Max+"). */
  planName: string;
  /** When the period credits renew, or null when the BFF sent no reset date. */
  renewalDateIso: string | null;
}

export function useCreditPlanContext(): CreditPlanContext {
  const { t } = useTranslation();
  const credits = useCredits();
  // The sidebar persists across SPA navigations, so these fetch once per load and stay cached — the
  // same two reads (and the same catalog rule) the drawer card uses, so the surfaces can't disagree.
  const { data: premium = null } = usePremium();
  const { data: plans = null } = usePlans();
  const account = credits.account;

  const isMaxPlus = activePlanIsMaxPlus(premium, plans);
  const paidPlanNameKey = planNameKeyOf(plans, activePremiumSubscription(premium)?.planId ?? null);
  // `: string` matters — without a contextual type the i18n `t` generic infers a nullable return for
  // the dynamic-key call and the whole chain widens to `string | null | undefined`.
  const planName: string = paidPlanNameKey ? t(paidPlanNameKey) : account?.planName || t('settings.plan.yourPlan');

  return { isFreePlan: account?.isFreePlan ?? true, isMaxPlus, planName, renewalDateIso: account?.resetDateIso ?? null };
}
